import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Avatar } from '@/components/ui/avatar';
import type { MentionInputRef } from '@/hooks/useMentionField';
import { users } from '@/lib/api/endpoints';
import type { UserPublic } from '@/lib/api/types';
import { keys } from '@/lib/queryClient';
import { cn } from '@/lib/utils';
import { useT } from '@/lib/i18n';

/**
 * The `@` picker.
 *
 * Selecting someone hands back a whole `UserPublic`. The caller sends
 * `assigned_to_user_id`; the text in the title is only ever decoration. The
 * backend deliberately does not parse handles out of prose — a typo must not
 * silently hand someone else's work to a stranger.
 */
export function MentionPicker({
  query,
  onSelect,
  onDismiss,
  inputRef,
}: {
  query: string;
  onSelect: (user: UserPublic) => void;
  onDismiss: () => void;
  /** The field this is attached to, so arrow keys can be forwarded to it. */
  inputRef: MentionInputRef;
}) {
  const t = useT();
  const trimmed = query.trim();

  const { data, isFetching } = useQuery({
    queryKey: keys.userSearch(trimmed),
    queryFn: ({ signal }) => users.search(trimmed, signal),
    // The endpoint requires two characters and is rate-limited; asking sooner
    // burns the budget on queries that cannot succeed.
    enabled: trimmed.length >= 2,
    staleTime: 30_000,
  });

  const results = useMemo(() => data ?? [], [data]);

  // The highlight resets when the query changes. Stored together with the query
  // it belongs to and reconciled during render, rather than in an effect that
  // would schedule a second render for every keystroke.
  const [selection, setSelection] = useState({ query: trimmed, index: 0 });
  const highlighted = selection.query === trimmed ? selection.index : 0;

  const move = useCallback(
    (delta: number, length: number) => {
      setSelection((current) => {
        const base = current.query === trimmed ? current.index : 0;
        return { query: trimmed, index: (base + delta + length) % length };
      });
    },
    [trimmed],
  );

  // Arrow keys and Enter belong to the picker while it is open, but focus stays
  // in the input so typing is never interrupted — so the handler listens on the
  // input itself, in the capture phase, ahead of any form submit.
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const onKeyDown = (rawEvent: Event) => {
      const event = rawEvent as KeyboardEvent;
      if (results.length === 0) {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          onDismiss();
        }
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        move(1, results.length);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        move(-1, results.length);
      } else if (event.key === 'Enter' || event.key === 'Tab') {
        const chosen = results[highlighted];
        if (chosen) {
          // Enter here means "pick this user", not "submit the form".
          event.preventDefault();
          event.stopPropagation();
          onSelect(chosen);
        }
      } else if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onDismiss();
      }
    };

    input.addEventListener('keydown', onKeyDown, { capture: true });
    return () => input.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [results, highlighted, move, onSelect, onDismiss, inputRef]);

  if (trimmed.length < 2) {
    return (
      <PickerShell>
        <p className="px-3 py-2 text-xs text-content-subtle">{t('mention.typeMore')}</p>
      </PickerShell>
    );
  }

  if (results.length === 0) {
    return (
      <PickerShell>
        <p className="px-3 py-2 text-xs text-content-subtle">
          {isFetching
            ? t('common.searching')
            : t('mention.noMatch', { query: trimmed })}
        </p>
      </PickerShell>
    );
  }

  return (
    <PickerShell>
      <ul role="listbox" aria-label={t('command.users')}>
        {results.map((user, index) => (
          <li key={user.id}>
            <button
              type="button"
              role="option"
              aria-selected={index === highlighted}
              // The input keeps focus, so selection is driven by mousedown —
              // a click would blur the field first and close the picker.
              onMouseDown={(event) => {
                event.preventDefault();
                onSelect(user);
              }}
              onMouseEnter={() => setSelection({ query: trimmed, index })}
              className={cn(
                'flex w-full items-center gap-2.5 px-3 py-1.5 text-left transition-surface',
                index === highlighted ? 'bg-surface-hover' : 'bg-transparent',
              )}
            >
              <Avatar user={user} size="md" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-content">
                  {user.display_name}
                </span>
                <span className="block truncate text-xs text-content-subtle">
                  @{user.username}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </PickerShell>
  );
}

function PickerShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto',
        'rounded-md border border-border bg-surface-raised py-1 shadow-lg shadow-black/10',
      )}
    >
      {children}
    </div>
  );
}
